import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Container,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOfferResponseMutation } from '../../services/onboardingApi';
import { useErrorModal } from '../../hooks/useErrorModal';

// Helper function to format date
const formatDate = (date, format) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(date).toLocaleDateString('en-US', options);
};

const OfferStatusAcceptance = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const offerId = searchParams.get('offerId');
  
  const [formData, setFormData] = useState({
    decision: '', // 'accept' or 'reject'
    fullName: '',
    signature: '',
    date: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
    rejectionReason: '',
  });
  
  const [errors, setErrors] = useState({});
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const [offerResponseMutation, { isLoading }] = useOfferResponseMutation();
  const { showError, ErrorModal } = useErrorModal();

  // Mock offer letter data - in real app, this would come from API
  const offerData = {
    employeeName: 'John Doe',
    position: 'Senior Software Engineer',
    department: 'Engineering',
    startDate: '2024-03-15',
    salary: '$120,000',
    location: 'San Francisco, CA',
    reportingTo: 'Jane Smith - Engineering Manager',
    offerDate: '2024-02-20',
    company: 'Tech Solutions Inc.',
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.decision) {
      newErrors.decision = 'Please select Accept or Reject';
    }
    
    if (formData.decision === 'accept') {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.signature.trim()) {
        newErrors.signature = 'Signature is required';
      }
      if (!formData.date) {
        newErrors.date = 'Date is required';
      }
    }
    
    if (formData.decision === 'reject' && !formData.rejectionReason.trim()) {
      newErrors.rejectionReason = 'Rejection reason is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        token,
        offerId,
        decision: formData.decision,
        ...(formData.decision === 'accept' && {
          fullName: formData.fullName,
          signature: formData.signature,
          date: formData.date,
        }),
        ...(formData.decision === 'reject' && {
          rejectionReason: formData.rejectionReason,
        }),
      };

      const result = await offerResponseMutation(payload).unwrap();
      
      if (result.success) {
        setSuccess(
          formData.decision === 'accept' 
            ? 'Offer accepted successfully! You will receive further instructions via email.'
            : 'Your response has been recorded. Thank you for letting us know.'
        );
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/thank-you');
        }, 3000);
      }
    } catch (err) {
      showError(err);
    }
  };

  const handleDecisionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      decision: value,
    }));
    
    if (value === 'reject') {
      setShowRejectDialog(true);
    }
  };

  const handleRejectConfirm = () => {
    setShowRejectDialog(false);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        <Grid container>
          {/* Left Side - Offer Letter */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                OFFER LETTER
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ color: '#424242' }}>
                {formatDate(new Date(offerData.offerDate))}
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Dear <strong>{offerData.employeeName}</strong>,
              </Typography>
              
              <Typography variant="body1" paragraph>
                We are pleased to offer you the position of <strong>{offerData.position}</strong> at <strong>{offerData.company}</strong>. 
                We were impressed with your background and believe you would be a valuable addition to our team.
              </Typography>
              
              <Typography variant="body1" paragraph>
                <strong>Position Details:</strong>
              </Typography>
              
              <Box sx={{ ml: 3, mb: 2 }}>
                <Typography variant="body2" paragraph>
                  • <strong>Position:</strong> {offerData.position}<br/>
                  • <strong>Department:</strong> {offerData.department}<br/>
                  • <strong>Start Date:</strong> {formatDate(new Date(offerData.startDate))}<br/>
                  • <strong>Salary:</strong> {offerData.salary} per annum<br/>
                  • <strong>Location:</strong> {offerData.location}<br/>
                  • <strong>Reporting to:</strong> {offerData.reportingTo}
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                This offer is contingent upon your completion of all required pre-employment documentation and background check.
              </Typography>
              
              <Typography variant="body1" paragraph>
                Please review this offer carefully and indicate your decision by signing below or contacting us if you have any questions.
              </Typography>
              
              <Typography variant="body1" paragraph>
                We are excited about the possibility of you joining our team!
              </Typography>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1">
                  Sincerely,<br/>
                  Jane Smith<br/>
                  Engineering Manager<br/>
                  {offerData.company}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          {/* Right Side - Acceptance Form */}
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 4, bgcolor: 'white', minHeight: '100vh', borderLeft: '1px solid #e0e0e0' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                Acknowledgment and Acceptance of Offer Letter
              </Typography>
              
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">Please select your decision:</FormLabel>
                  <RadioGroup
                    name="decision"
                    value={formData.decision}
                    onChange={(e) => handleDecisionChange(e.target.value)}
                  >
                    <FormControlLabel 
                      value="accept" 
                      control={<Radio />} 
                      label="I accept this offer" 
                    />
                    <FormControlLabel 
                      value="reject" 
                      control={<Radio />} 
                      label="I decline this offer" 
                    />
                  </RadioGroup>
                  {errors.decision && (
                    <Typography variant="caption" color="error">
                      {errors.decision}
                    </Typography>
                  )}
                </FormControl>

                {formData.decision === 'accept' && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                      Please fill in the following details:
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      margin="normal"
                      required
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      disabled={isLoading}
                    />
                    
                    <TextField
                      fullWidth
                      label="Signature"
                      name="signature"
                      value={formData.signature}
                      onChange={handleChange}
                      margin="normal"
                      required
                      placeholder="Type your full name as signature"
                      error={!!errors.signature}
                      helperText={errors.signature}
                      disabled={isLoading}
                    />
                    
                    <TextField
                      fullWidth
                      label="Date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleChange}
                      margin="normal"
                      required
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.date}
                      helperText={errors.date}
                      disabled={isLoading}
                    />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                      By signing above, you confirm that you have read, understood, and accepted the terms of this offer letter.
                    </Typography>
                  </>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading || !formData.decision}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Submit Response'}
                </Button>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Rejection Reason Dialog */}
      <Dialog open={showRejectDialog} onClose={handleRejectConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Reason for Declining</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for declining this offer (optional but appreciated):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for declining"
            name="rejectionReason"
            value={formData.rejectionReason}
            onChange={handleChange}
            placeholder="e.g., Accepted another offer, compensation, location, etc."
            error={!!errors.rejectionReason}
            helperText={errors.rejectionReason}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectConfirm}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleRejectConfirm();
              // Form can be submitted without rejection reason if user chooses
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorModal />
    </Container>
  );
};

export default OfferStatusAcceptance;
